<script setup lang="ts">
import { ShieldCheck, Loader2, KeyRound, Smartphone, Mail } from "lucide-vue-next";
import { getAuth, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";

type Method = "password" | "totp" | "email_otp";

const { isOpen, completeReauth, cancelReauth } = useReauthModal();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const selectedMethod = ref<Method | null>(null);
const isSubmitting = ref(false);
const error = ref<string | null>(null);

const password = ref("");
const totpCode = ref("");
const emailOtpCode = ref("");
const emailOtpDestination = ref<string | null>(null);
const emailOtpCooldown = ref(0);

let cooldownTimer: ReturnType<typeof setInterval> | null = null;

watch(isOpen, (open) => {
  if (open) return;
  selectedMethod.value = null;
  isSubmitting.value = false;
  error.value = null;
  password.value = "";
  totpCode.value = "";
  emailOtpCode.value = "";
  emailOtpDestination.value = null;
  emailOtpCooldown.value = 0;
  if (cooldownTimer) {
    clearInterval(cooldownTimer);
    cooldownTimer = null;
  }
});

function startCooldown(seconds: number) {
  emailOtpCooldown.value = seconds;
  if (cooldownTimer) clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    emailOtpCooldown.value -= 1;
    if (emailOtpCooldown.value <= 0) {
      clearInterval(cooldownTimer!);
      cooldownTimer = null;
    }
  }, 1000);
}

async function sendEmailOtp() {
  isSubmitting.value = true;
  error.value = null;
  try {
    const res = await $fetch<{ destination: string; retryAfterSeconds: number }>("/api/auth/otp/send-reauth", {
      method: "POST",
      headers: await getAuthHeader(),
    });
    emailOtpDestination.value = res.destination;
    startCooldown(res.retryAfterSeconds);
  } catch (err: unknown) {
    const apiErr = err as { data?: { data?: { retryAfterSeconds?: number } }; message?: string };
    const retryAfter = apiErr?.data?.data?.retryAfterSeconds;
    if (retryAfter) {
      startCooldown(retryAfter);
      error.value = `Please wait ${retryAfter}s before requesting a new code.`;
    } else {
      error.value = "Failed to send code. Please try again.";
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function submitPassword() {
  if (!password.value) return;
  isSubmitting.value = true;
  error.value = null;
  try {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || !currentUser.value?.email) throw new Error("No active Firebase session");

    const credential = EmailAuthProvider.credential(currentUser.value.email, password.value);
    await reauthenticateWithCredential(firebaseUser, credential);
    const firebaseToken = await firebaseUser.getIdToken(true);

    const authStore = useAuthStore();
    const res = await $fetch<{ accessToken: string }>("/api/auth/reauth", {
      method: "POST",
      headers: await getAuthHeader(),
      body: { firebaseToken },
    });
    authStore.setSession(res.accessToken);
    completeReauth();
  } catch {
    error.value = "Incorrect password. Please try again.";
  } finally {
    isSubmitting.value = false;
  }
}

async function submitTotp() {
  if (totpCode.value.length !== 6) return;
  isSubmitting.value = true;
  error.value = null;
  try {
    const authStore = useAuthStore();
    const res = await $fetch<{ accessToken: string }>("/api/auth/reauth", {
      method: "POST",
      headers: await getAuthHeader(),
      body: { totpCode: totpCode.value },
    });
    authStore.setSession(res.accessToken);
    completeReauth();
  } catch {
    error.value = "Invalid code. Please try again.";
  } finally {
    isSubmitting.value = false;
  }
}

async function submitEmailOtp() {
  if (emailOtpCode.value.length !== 6) return;
  isSubmitting.value = true;
  error.value = null;
  try {
    const authStore = useAuthStore();
    const res = await $fetch<{ accessToken: string }>("/api/auth/reauth", {
      method: "POST",
      headers: await getAuthHeader(),
      body: { emailOtp: emailOtpCode.value },
    });
    authStore.setSession(res.accessToken);
    completeReauth();
  } catch {
    error.value = "Invalid or expired code. Please try again.";
  } finally {
    isSubmitting.value = false;
  }
}

const canSubmit = computed(() => {
  if (isSubmitting.value) return false;
  if (selectedMethod.value === "password") return password.value.length > 0;
  if (selectedMethod.value === "totp") return totpCode.value.length === 6;
  if (selectedMethod.value === "email_otp") return emailOtpCode.value.length === 6;
  return false;
});

function handleSubmit() {
  if (selectedMethod.value === "password") submitPassword();
  else if (selectedMethod.value === "totp") submitTotp();
  else if (selectedMethod.value === "email_otp") submitEmailOtp();
}
</script>

<template>
  <Dialog :open="isOpen">
    <DialogContent :hide-close-button="true" class="max-w-sm">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <ShieldCheck class="w-5 h-5 text-red-500" />
          Step-up verification required
        </DialogTitle>
        <DialogDescription>
          This action requires additional verification. Choose a method to continue.
        </DialogDescription>
      </DialogHeader>

      <!-- method selection -->
      <div v-if="!selectedMethod" class="grid gap-2">
        <button
          class="flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover:bg-muted"
          @click="selectedMethod = 'password'"
        >
          <KeyRound class="w-4 h-4 shrink-0 text-muted-foreground" />
          <div>
            <p class="text-sm font-medium">Password</p>
            <p class="text-xs text-muted-foreground">Re-enter your account password</p>
          </div>
        </button>
        <button
          class="flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover:bg-muted"
          @click="selectedMethod = 'totp'"
        >
          <Smartphone class="w-4 h-4 shrink-0 text-muted-foreground" />
          <div>
            <p class="text-sm font-medium">Authenticator app</p>
            <p class="text-xs text-muted-foreground">Enter the 6-digit code from your app</p>
          </div>
        </button>
        <button
          class="flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover:bg-muted"
          @click="selectedMethod = 'email_otp'"
        >
          <Mail class="w-4 h-4 shrink-0 text-muted-foreground" />
          <div>
            <p class="text-sm font-medium">Email code</p>
            <p class="text-xs text-muted-foreground">Send a one-time code to your email</p>
          </div>
        </button>
      </div>

      <!-- password -->
      <div v-else-if="selectedMethod === 'password'" class="space-y-3">
        <p class="text-sm text-muted-foreground">
          Enter the password for <strong>{{ currentUser?.email }}</strong>
        </p>
        <Input
          v-model="password"
          type="password"
          placeholder="Your password"
          autocomplete="current-password"
          @keydown.enter="handleSubmit"
        />
        <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
      </div>

      <!-- totp -->
      <div v-else-if="selectedMethod === 'totp'" class="space-y-3">
        <p class="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
        <Input
          v-model="totpCode"
          placeholder="000000"
          maxlength="6"
          inputmode="numeric"
          class="text-center tracking-widest text-lg"
          @keydown.enter="handleSubmit"
        />
        <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
      </div>

      <!-- email otp -->
      <div v-else-if="selectedMethod === 'email_otp'" class="space-y-3">
        <div v-if="!emailOtpDestination" class="text-center py-2">
          <p class="text-sm text-muted-foreground mb-3">
            We'll send a one-time code to your registered email address.
          </p>
          <Button variant="outline" :disabled="isSubmitting" @click="sendEmailOtp">
            <Loader2 v-if="isSubmitting" class="mr-2 w-4 h-4 animate-spin" />
            Send code
          </Button>
        </div>
        <template v-else>
          <p class="text-sm text-muted-foreground">
            A code was sent to <strong>{{ emailOtpDestination }}</strong>. Enter it below.
          </p>
          <Input
            v-model="emailOtpCode"
            placeholder="000000"
            maxlength="6"
            inputmode="numeric"
            class="text-center tracking-widest text-lg"
            @keydown.enter="handleSubmit"
          />
          <p v-if="error" class="text-sm text-red-500">{{ error }}</p>
          <Button
            variant="ghost"
            size="sm"
            class="w-full text-xs"
            :disabled="emailOtpCooldown > 0 || isSubmitting"
            @click="sendEmailOtp"
          >
            <span v-if="emailOtpCooldown > 0">Resend in {{ emailOtpCooldown }}s</span>
            <span v-else>Resend code</span>
          </Button>
        </template>
      </div>

      <DialogFooter class="gap-2">
        <Button variant="outline" :disabled="isSubmitting" @click="selectedMethod ? (selectedMethod = null) : cancelReauth()">
          {{ selectedMethod ? "Back" : "Cancel" }}
        </Button>
        <Button
          v-if="selectedMethod && !(selectedMethod === 'email_otp' && !emailOtpDestination)"
          variant="destructive"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          <Loader2 v-if="isSubmitting" class="mr-2 w-4 h-4 animate-spin" />
          Verify
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
